import { isEmpty } from "lodash";

import { Box, Table, Typography } from "components";
import { PageContent } from "layouts";

import { ReactComponent as AddIcon } from "assets/icon/add-icon.svg";
import { ReactComponent as EditIcon } from "assets/icon/edit.svg";

import { compose, withAuthorize, withHooks, withTranslation } from "enhancers";

import { gql, homePath, paths } from "utils/helper";

import {
  convertActiveInactiveToBoolean,
  filterOutAllOption,
} from "constants/objects/claim_request_mapping_status";
import { AppColor } from "theme/app-color";
import PartnerHospitalFilters, {
  partnerHospitalListFilterInitialValues,
} from "./PartnerHospitalFilters";
import { PERMISSIONS } from "constants/enums/permissions";

interface PartnerHospital {
  id: string;
  name: string;
  hospitalType: string;
  description?: string;
  startEffectiveDate: string;
  endEffectiveDate: string;
  termAndCondition: string;
  isActive: boolean;
}

interface PartnerHospitalIndexPageProps {
  t: (key: string) => string;
  title: string;
  breadcrumbs: Array<{ path: string | null; label: string }>;
  pageActions: Array<any>;
  columns: Array<any>;
  tableData: Array<any>;
  loading: boolean;
}

export const PARTNER_HOSPITAL_API = {
  FETCH_PARTNER_HOSPITALS: gql`
    query FETCH_PARTNER_HOSPITALS(
      $name: String
      $isActive: Boolean
      $hospitalType: String
    ) {
      partnerHospitalList(
        filters: {
          name: $name
          isActive: $isActive
          hospitalType: $hospitalType
        }
      ) {
        id
        name
        hospitalType
        description
        startEffectiveDate
        endEffectiveDate
        termAndCondition
        isActive
      }
    }
  `,
};

const PartnerHospitalIndexPage = (props: PartnerHospitalIndexPageProps) => (
  <PageContent
    title={props.t(".title")}
    pageActions={props.pageActions}
    breadcrumbs={props.breadcrumbs}
    filter={<PartnerHospitalFilters />}
  >
    <Box width="100%">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" style={{ marginBottom: 16 }}>
          {props.t(".list")}
        </Typography>
        <Typography
          variant="caption"
          color={AppColor["Text/Secondary"]}
          style={{ marginBottom: 16, textAlign: "right" }}
        >
          {props.t(".infoText1")}
          <br />
          {props.t(".infoText2")}
        </Typography>
      </Box>

      <Table
        columns={props.columns}
        rows={props.tableData}
        loading={props.loading}
        density="compact"
        autoHeight
        disableSelectionOnClick
      />
    </Box>
  </PageContent>
);

const createBreadcrumbs = (title: string, t: (key: string) => string) => [
  { path: homePath(), label: t(".home") },
  { path: null, label: title },
];

const createPageActions = (
  hasEditPermission: boolean,
  partnerHospitalList: any[],
  t: (key: string) => string
) => {
  if (!hasEditPermission) return [];

  const actions = [
    {
      children: t(".pdfSetting"),
      variant: "outlined",
      color: AppColor["Text/Primary Text"],
      permittedRoles: [PERMISSIONS.PARTNER_MANAGEMENT_EDIT],
      onClick: () => {
        paths.pdfSettingPath().push();
      },
    },
    {
      children: t(".addPartnerHospital"),
      startIcon: <AddIcon />,
      color: "primary",
      permittedRoles: [PERMISSIONS.PARTNER_MANAGEMENT_EDIT],
      onClick: () => {
        paths.partnerHospitalCreatePath().push();
      },
    },
  ];
  return actions;
};

const createTableColumns = (t: (key: string) => string) => [
  {
    field: "name",
    headerName: t(".name"),
    flex: 1,
    minWidth: 200,
  },
  {
    field: "hospitalType",
    headerName: t(".hospitalType"),
    width: 100,
    renderCell: (params: any) => t(`.${params.row.hospitalType}`),
  },
  {
    field: "description",
    headerName: t(".description"),
    flex: 1,
    minWidth: 250,
  },
  {
    field: "isActive",
    headerName: t(".status"),
    width: 120,
    type: "tag",
  },
  {
    width: 100,
    field: "actions",
    headerName: " ",
    filterable: false,
    sortable: false,
    type: "actions",
  },
];

const transformTableData = (
  t: (key: string) => string,
  partnerHospitalList: PartnerHospital[],
  hasEditPermission: boolean
) => {
  if (!partnerHospitalList) return [];

  return partnerHospitalList.map((partnerHospital) => ({
    ...partnerHospital,
    description: partnerHospital.description || "-",
    isActive: partnerHospital.isActive ? t(".active") : t(".inactive"),
    actions: hasEditPermission
      ? [
          {
            Icon: EditIcon,
            onClick: (row: any) => {
              paths.partnerHospitalDetailPath(row.id).push();
            },
          },
        ]
      : [],
  }));
};

const validateQueryParams = (queryParams: any) => {
  if (isEmpty(queryParams)) return;

  const allowedKeys = Object.keys(partnerHospitalListFilterInitialValues);
  const hasInvalidKey = Object.keys(queryParams).some(
    (key) => !allowedKeys.includes(key)
  );

  if (hasInvalidKey) {
    paths.partnerHospitalPath().push();
  }
};

const enhancer = compose(
  withAuthorize({
    // permissions: [PERMISSIONS.PARTNER_HOSPITAL_MANAGEMENT_READ],
  }),
  withTranslation({ prefix: "pages.main.partnerHospitals.index" }),
  withHooks((props: any, hooks: any) => {
    const { useQuery, useMemo, useUrlParam, useEffect } = hooks;
    const { t, hasPermission } = props;

    const queryParams = useUrlParam();

    const { data, loading } = useQuery(
      PARTNER_HOSPITAL_API.FETCH_PARTNER_HOSPITALS,
      {
        variables: convertActiveInactiveToBoolean(
          filterOutAllOption(queryParams)
        ),
        fetchPolicy: "network-only",
      }
    );

    useEffect(() => {
      validateQueryParams(queryParams);
    }, [queryParams]);

    const hasEditPermission = hasPermission([
      PERMISSIONS.PARTNER_MANAGEMENT_EDIT,
    ]);

    const title = t(".title");

    const breadcrumbs = useMemo(() => createBreadcrumbs(title, t), [title, t]);

    const pageActions = useMemo(
      () => createPageActions(hasEditPermission, data?.partnerHospitalList, t),
      [hasEditPermission, data, t]
    );

    const columns = useMemo(() => createTableColumns(t), [t]);

    const tableData = useMemo(
      () => transformTableData(t, data?.partnerHospitalList, hasEditPermission),
      [data, t, hasEditPermission]
    );

    return {
      title,
      breadcrumbs,
      pageActions,
      columns,
      tableData,
      loading,
      t,
    };
  })
);

export default enhancer(PartnerHospitalIndexPage);
